import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Checkbox } from '../inputs/Checkbox/Checkbox';
import './AccordionStyles.scss';
export function Accordion({ title, previewContent, actions = [], actionsContent, children, variant = 'inline', defaultExpanded = false, isExpanded: controlledIsExpanded, className = '', onToggle, selectable = false, checkboxProps }) {
    const isControlled = controlledIsExpanded !== undefined;
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const detailsRef = useRef(null);
    useEffect(() => {
        if (!isControlled) {
            setInternalExpanded(defaultExpanded);
        }
    }, [defaultExpanded, isControlled]);
    const isExpanded = isControlled ? controlledIsExpanded : internalExpanded;
    const handleToggle = (event) => {
        const newExpandedState = event.currentTarget.open;
        setInternalExpanded(newExpandedState);
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(newExpandedState);
    };
    const handleSummaryClick = (event) => {
        if (!isControlled)
            return;
        event.preventDefault();
        onToggle === null || onToggle === void 0 ? void 0 : onToggle(!isExpanded);
    };
    const handleActionClick = (event, action) => {
        var _a, _b;
        event.stopPropagation();
        (_b = (_a = action.buttonProps) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
    };
    const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, [role="button"]';
    const handleActionsContentClick = (event) => {
        // Always stop the click from reaching <summary> so actionsContent never toggles
        // the accordion. Only also preventDefault() when the click didn't land on a
        // native interactive element — a real <a>/<input>/etc. inside actionsContent
        // keeps its own default action (navigation, checking, ...); plain filler content
        // has no default action to preserve, so suppressing the summary's native
        // disclosure toggle there is safe.
        event.stopPropagation();
        const interactiveTarget = event.target.closest(INTERACTIVE_SELECTOR);
        const clickedInteractiveElement = interactiveTarget && event.currentTarget.contains(interactiveTarget);
        if (!clickedInteractiveElement) {
            event.preventDefault();
        }
    };
    const isSelected = selectable && (checkboxProps === null || checkboxProps === void 0 ? void 0 : checkboxProps.checked);
    const accordionClasses = [
        'qmb-ui-accordion',
        `qmb-ui-accordion--${variant}`,
        isExpanded ? 'qmb-ui-accordion--expanded' : 'qmb-ui-accordion--collapsed',
        selectable ? 'qmb-ui-accordion--selectable' : '',
        isSelected ? 'qmb-ui-accordion--selected' : '',
        className
    ]
        .filter(Boolean)
        .join(' ');
    return (React.createElement("details", { ref: detailsRef, className: accordionClasses, onToggle: isControlled ? undefined : handleToggle, open: isExpanded },
        React.createElement("summary", { className: "qmb-ui-accordion__summary", onClick: handleSummaryClick },
            React.createElement("div", { className: "qmb-ui-accordion__header" },
                React.createElement("div", { className: "qmb-ui-accordion__header-left" },
                    selectable && checkboxProps && (React.createElement("div", { className: "qmb-ui-accordion__checkbox-wrapper" },
                        React.createElement(Checkbox, { id: checkboxProps.id, checked: checkboxProps.checked, onChange: (checked) => {
                                checkboxProps.onChange(checked);
                            }, onClick: (e) => {
                                var _a;
                                e.stopPropagation();
                                (_a = checkboxProps.onClick) === null || _a === void 0 ? void 0 : _a.call(checkboxProps, e);
                            }, className: checkboxProps.className, disabled: checkboxProps.disabled }))),
                    React.createElement("span", { className: "qmb-ui-accordion__chevron", "aria-hidden": "true" },
                        React.createElement("i", { className: "fa-solid fa-chevron-right" })),
                    React.createElement("div", { className: "qmb-ui-accordion__title-container" },
                        React.createElement("span", { className: "qmb-ui-accordion__title" }, title),
                        previewContent &&
                            !isExpanded &&
                            (typeof previewContent === 'string' ? (React.createElement("span", { className: "qmb-ui-accordion__preview-text" }, previewContent)) : (React.createElement("div", { className: "qmb-ui-accordion__preview-content" }, previewContent)))),
                    variant === 'section' && React.createElement("div", { className: "qmb-ui-accordion__divider-line" })),
                (actionsContent || actions.length > 0) && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- stops clicks on already-interactive children from toggling the summary; not itself interactive
                React.createElement("div", { className: "qmb-ui-accordion__actions", onClick: handleActionsContentClick },
                    actionsContent,
                    actions.map((action, index) => {
                        var _a, _b, _c, _d, _e;
                        return (React.createElement(Button, { key: index, id: (_a = action.buttonProps) === null || _a === void 0 ? void 0 : _a.id, variant: ((_b = action.buttonProps) === null || _b === void 0 ? void 0 : _b.variant) || 'default', onClick: (event) => handleActionClick(event, action), disabled: (_c = action.buttonProps) === null || _c === void 0 ? void 0 : _c.disabled, type: ((_d = action.buttonProps) === null || _d === void 0 ? void 0 : _d.type) || 'button', className: `qmb-ui-accordion__action-button ${((_e = action.buttonProps) === null || _e === void 0 ? void 0 : _e.className) || ''}` },
                            action.icon && React.createElement("span", { className: "qmb-ui-accordion__action-icon" }, action.icon),
                            action.label));
                    }))))),
        React.createElement("div", { className: "qmb-ui-accordion__content" }, children)));
}
